const upsertMeta = (selector, attribute, key, content) => {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.setAttribute('content', String(content || ''));
};

export const siteOrigin = () => {
  try {
    const url = new URL(process.env.REACT_APP_SITE_URL || 'https://eulua.com');
    return /^https?:$/.test(url.protocol) ? url.origin : 'https://eulua.com';
  } catch { return 'https://eulua.com'; }
};

const absoluteUrl = (value) => {
  if (!value) return '';
  try {
    const url = new URL(value, siteOrigin());
    return /^https?:$/.test(url.protocol) ? url.toString() : '';
  } catch {
    return '';
  }
};

export const setStructuredData = (id, data) => {
  let script = document.head.querySelector(`#${id}`);
  if (!data) {
    script?.remove();
    return;
  }
  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = id;
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(data);
};

export const removeStructuredData = (id) => {
  document.head.querySelector(`#${id}`)?.remove();
};

export const updatePageSeo = ({
  title,
  description,
  canonicalPath,
  alternateEnPath,
  alternateEsPath,
  image,
  type = 'website',
  noindex = false,
  nofollow = noindex,
  language,
}) => {
  const canonicalUrl = absoluteUrl(canonicalPath || window.location.pathname);
  // Every page falls back to the default social share image so og:image /
  // twitter:image are always present (bots that don't run JS still get the
  // static defaults baked into public/index.html; this keeps client-side
  // navigations consistent with that fallback).
  const imageUrl = absoluteUrl(image || '/og-image.png');
  // Fall back to the canonical URL itself when a page hasn't told us its
  // language-specific counterpart (e.g. it isn't mirrored under /es yet).
  const enUrl = absoluteUrl(alternateEnPath || canonicalPath || window.location.pathname);
  const esUrl = absoluteUrl(alternateEsPath || canonicalPath || window.location.pathname);

  document.title = title;
  document.documentElement.lang = language || document.documentElement.lang || 'en';
  upsertMeta('meta[name="description"]', 'name', 'description', description);
  upsertMeta('meta[name="robots"]', 'name', 'robots', noindex
    ? `noindex, ${nofollow ? 'nofollow' : 'follow'}`
    : 'index, follow, max-image-preview:large');
  upsertMeta('meta[property="og:title"]', 'property', 'og:title', title);
  upsertMeta('meta[property="og:description"]', 'property', 'og:description', description);
  upsertMeta('meta[property="og:type"]', 'property', 'og:type', type);
  upsertMeta('meta[property="og:url"]', 'property', 'og:url', canonicalUrl);
  const activeLanguage = (language || document.documentElement.lang) === 'es' ? 'es' : 'en';
  upsertMeta('meta[property="og:locale"]', 'property', 'og:locale', activeLanguage === 'es' ? 'es_ES' : 'en_US');
  upsertMeta('meta[property="og:locale:alternate"]', 'property', 'og:locale:alternate', activeLanguage === 'es' ? 'en_US' : 'es_ES');
  upsertMeta('meta[name="twitter:card"]', 'name', 'twitter:card', imageUrl ? 'summary_large_image' : 'summary');
  upsertMeta('meta[name="twitter:title"]', 'name', 'twitter:title', title);
  upsertMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description);

  if (imageUrl) {
    upsertMeta('meta[property="og:image"]', 'property', 'og:image', imageUrl);
    upsertMeta('meta[name="twitter:image"]', 'name', 'twitter:image', imageUrl);
    upsertMeta('meta[property="og:image:alt"]', 'property', 'og:image:alt', title);
    upsertMeta('meta[name="twitter:image:alt"]', 'name', 'twitter:image:alt', title);
    if (image) {
      document.head.querySelector('meta[property="og:image:width"]')?.remove();
      document.head.querySelector('meta[property="og:image:height"]')?.remove();
    }
  } else {
    document.head.querySelector('meta[property="og:image"]')?.remove();
    document.head.querySelector('meta[name="twitter:image"]')?.remove();
  }

  let canonical = document.head.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }
  canonical.setAttribute('href', canonicalUrl);

  if (noindex) {
    ['es', 'en', 'x-default'].forEach((hreflang) => {
      document.head.querySelector(`link[rel="alternate"][hreflang="${hreflang}"]`)?.remove();
    });
  } else {
    [['es', esUrl], ['en', enUrl], ['x-default', enUrl]].forEach(([hreflang, href]) => {
      let alternate = document.head.querySelector(`link[rel="alternate"][hreflang="${hreflang}"]`);
      if (!alternate) {
        alternate = document.createElement('link');
        alternate.setAttribute('rel', 'alternate');
        alternate.setAttribute('hreflang', hreflang);
        document.head.appendChild(alternate);
      }
      alternate.setAttribute('href', href);
    });
  }
};
